import { useEffect, useState } from "react";
import { Box, MenuItem, Select, Slider, Switch, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useSetting } from "../../hooks/useSettings";
import { SettingKey } from "../../settings/schema";
import { RegistryEntry } from "../../settings/types";
import { settingsStore } from "../../settings/store";

const toggleSx = {
  "& .MuiToggleButton-root": { fontSize: 11, px: 1.5, py: 0.5, textTransform: "none" },
};

interface Props {
  entry: RegistryEntry;
  onError: (msg: string) => void;
}

export default function SettingControl({ entry, onError }: Props) {
  const [value, setValue] = useSetting(entry.key as SettingKey);
  const def = entry.def;

  const commit = (v: unknown) => {
    try {
      (setValue as (x: unknown) => void)(v);
      // 화면 설정은 저장 버튼 없이 바로 저장
      if (def.group === "appearance") {
        settingsStore.saveNow(entry.key as SettingKey).catch((e) => onError((e as Error).message));
      }
    } catch (e) {
      onError((e as Error).message);
    }
  };

  if (def.type === "boolean") {
    return (
      <Switch
        size="small"
        checked={Boolean(value)}
        disabled={entry.locked}
        onChange={(e) => commit(e.target.checked)}
      />
    );
  }

  if (def.type === "select") {
    // 3개 이하는 토글, 그 이상은 드롭다운
    if (def.options.length <= 3) {
      return (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={value}
          disabled={entry.locked}
          onChange={(_, v) => v && commit(v)}
          sx={toggleSx}
        >
          {def.options.map((o) => (
            <ToggleButton key={o.value} value={o.value}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
    }
    return (
      <Select
        size="small"
        value={value}
        disabled={entry.locked}
        onChange={(e) => commit(e.target.value)}
        sx={{ fontSize: 12, minWidth: 110 }}
      >
        {def.options.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12 }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (def.type === "number") {
    return <NumberControl entry={entry} value={Number(value)} onCommit={commit} />;
  }

  return null;
}

function NumberControl({
  entry,
  value,
  onCommit,
}: {
  entry: RegistryEntry;
  value: number;
  onCommit: (v: number) => void;
}) {
  const def = entry.def as Extract<RegistryEntry["def"], { type: "number" }>;
  const [local, setLocal] = useState(value);

  useEffect(() => setLocal(value), [value]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: 140 }}>
      <Slider
        size="small"
        min={def.min ?? 0}
        max={def.max ?? 100}
        value={local}
        disabled={entry.locked}
        onChange={(_, v) => setLocal(v as number)}
        onChangeCommitted={(_, v) => onCommit(v as number)}
        sx={{ flex: 1 }}
      />
      <Typography fontSize={12} color="text.secondary" sx={{ minWidth: 28 }}>
        {local}
      </Typography>
    </Box>
  );
}
